
import { defineComponent, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import { Apollo, Notify } from '@/core/services';
import Table from '@/components/Table/Table.vue';
import { GET_WMS_WEBHOOK_LOG, GET_WMS_WEBHOOK_LOGS } from '@/modules/wms/graphql/Queries';
import { RESEND_WMS_WEBHOOK } from '@/modules/wms/graphql/Mutations';
import { useWms } from '@/modules/wms/helpers';

export default defineComponent({
    name: 'wms-logs',
    components: { Table },
    setup() {
        const store = useStore();
        const i18n = useI18n();
        // with WMS off nothing is sent to Smart: resend is disabled
        const { wmsManagesProducts } = useWms();
        const loader = ref(false);
        const logs = ref<any[]>([]);
        const pagination = ref({}) as Record<any, any>;
        const log = ref(null) as Record<any, any>;
        let modal: any = null;

        const emptyFilters = () => ({ direction: '', entity: '', status: '', search: '', date_from: '', date_to: '' });
        const filters = ref(emptyFilters());

        const directions = [
            { label: 'message.WMS_DIRECTION_IN', value: 'in' },
            { label: 'message.WMS_DIRECTION_OUT', value: 'out' }
        ];
        const entities = [
            { label: 'message.WMS_ENTITY_UNITS', value: 'units' },
            { label: 'message.WMS_ENTITY_CATEGORIES', value: 'categories' },
            { label: 'message.WMS_ENTITY_PRODUCTS', value: 'products' },
            { label: 'message.WMS_ENTITY_PRICE_LISTS', value: 'price-lists' },
            { label: 'message.WMS_ENTITY_STOCK', value: 'stock' },
            { label: 'message.WMS_ENTITY_PRODUCT_GROUPS', value: 'product-groups' },
            { label: 'message.WMS_ENTITY_PRODUCT_GROUP_PRODUCTS', value: 'product-group-products' },
            { label: 'message.WMS_ENTITY_STOCK_DEDUCTION', value: 'stock-deduction' }
        ];
        const statuses = ['success', 'partial', 'queued', 'failed', 'received'];

        const columns = [
            { label: 'message.ID', key: 'id', width: true },
            { label: 'message.CREATED_AT', key: 'created_at' },
            { label: 'message.WMS_DIRECTION', key: 'direction' },
            { label: 'message.WMS_ENTITY', key: 'entity' },
            { label: 'message.STATUS', key: 'status' },
            { label: 'message.WMS_ATTEMPT', key: 'attempt' },
            { label: 'message.ACTIONS', key: 'actions', textAlignment: 'end' }
        ];

        const formatDate = (value: string) => (value ? new Date(value).toLocaleString() : '');
        const statusColors: Record<string, string> = { success: 'success', partial: 'warning', queued: 'primary', failed: 'danger' };
        const statusColor = (status: string) => statusColors[status] || 'secondary';
        const entityLabel = (entity: string) => {
            const option = entities.find(item => item.value == entity);
            return option ? i18n.t(option.label) : entity;
        };

        // JSON bodies indented; anything else as it is
        const pretty = (value: any) => {
            if (value == null || value === '') {
                return '';
            }
            if (typeof value !== 'string') {
                return JSON.stringify(value, null, 2);
            }
            try {
                return JSON.stringify(JSON.parse(value), null, 2);
            } catch (e) {
                return value;
            }
        };

        const getLogs = (page = 1) => {
            loader.value = true;
            const variables = { page: page, limit: 20 } as Record<any, any>;
            Object.entries(filters.value).forEach(([key, value]) => {
                if (value) {
                    variables[key] = value;
                }
            });
            Apollo.query({ query: GET_WMS_WEBHOOK_LOGS, fetchPolicy: 'network-only', variables })
                .then(({ data }) => {
                    logs.value = data?.wms_webhook_logs?.data || [];
                    pagination.value = data?.wms_webhook_logs || {};
                })
                .catch(() => Notify.error(i18n.t('message.SOMETHING_WENT_WRONG')))
                .finally(() => (loader.value = false));
        };

        const search = () => getLogs(1);
        const reset = () => {
            filters.value = emptyFilters();
            getLogs(1);
        };

        const openLog = (id: number) => {
            Apollo.query({ query: GET_WMS_WEBHOOK_LOG, fetchPolicy: 'network-only', variables: { id } })
                .then(({ data }) => {
                    log.value = JSON.parse(data.wms_webhook_log);
                    if (!modal) {
                        modal = new Modal(document.getElementById('wms_log_modal') as HTMLElement);
                    }
                    modal.show();
                })
                .catch(() => Notify.error(i18n.t('message.SOMETHING_WENT_WRONG')));
        };

        const resend = (id: number) => {
            if (!wmsManagesProducts.value) {
                return;
            }
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.WMS_RESEND_CONFIRM')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.YES'),
                    cancelButtonText: i18n.t('message.NO'),
                    type: 'warning'
                })
                .then(() => {
                    loader.value = true;
                    return Apollo.mutate({ mutation: RESEND_WMS_WEBHOOK, variables: { id } }).then(({ data }) => {
                        const attempt = JSON.parse(data.resend_wms_webhook);
                        attempt.status == 'success' ? Notify.success(i18n.t('message.WMS_RESEND_SUCCESS')) : Notify.error(i18n.t('message.WMS_RESEND_FAILED'));
                        if (log.value) {
                            openLog(attempt.id || id);
                        }
                        getLogs(pagination.value.current_page || 1);
                    });
                })
                .catch(() => (loader.value = false));
        };

        onMounted(() => getLogs(1));

        return {
            loader,
            logs,
            pagination,
            log,
            wmsManagesProducts,
            filters,
            directions,
            entities,
            statuses,
            columns,
            formatDate,
            statusColor,
            entityLabel,
            pretty,
            getLogs,
            search,
            reset,
            openLog,
            resend
        };
    }
});
